import {
  forwardRef,
  useState,
  useMemo,
  useRef,
  useImperativeHandle,
  useEffect,
  useCallback
} from 'react';
import type { PropsWithoutRef, ChangeEvent, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Flex,
  Icon,
  registerIcon,
  useI18n,
  defaultThemeProp,
  MenuButton,
  Input,
  Modal,
  useModalManager,
  useModalContext,
  ComboBox,
  menuHelpers,
  createStringMatcher,
  FileList,
  createUID,
  Checkbox,
  useLiveLog,
  usePrevious,
  getActiveElement,
  isInstance,
  ExpandCollapse
} from '@pega/cosmos-react-core';
import type {
  ModalMethods,
  ModalProps,
  MenuItemProps,
  MenuProps,
  FileItemProps
} from '@pega/cosmos-react-core';
import { Editor, StyledRichTextEditor } from '@pega/cosmos-react-rte';
import type { EditorState, EditorProps } from '@pega/cosmos-react-rte';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';
import * as replyAllIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/reply-all.icon';
import * as replyIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/reply.icon';
import * as forwardIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/forward.icon';
import {
  StyledModalChildrenWrap,
  StyledModalContent
} from '@pega/cosmos-react-core/lib/components/Modal/Modal.styles';
import {
  StyledEditorRoot,
  StyledEditorContainer,
  StyledEditorIframe
} from '@pega/cosmos-react-rte/lib/components/Editor/Editor.styles';
import type { ComboBoxHandleValue } from '@pega/cosmos-react-core/lib/components/ComboBox/ComboBox.types';

import type { EmailComposerProps } from './Email.types';
import EmailSelector from './EmailSelector';

registerIcon(paperClipIcon, replyAllIcon, replyIcon, forwardIcon);

export const StyledEmailComposer = styled.div<EmailComposerProps>(({ progress }) => {
  return css`
    ${progress
      ? css`
          visibility: hidden;
        `
      : css`
          visibility: visible;
        `}
  `;
});

export const StyledResponseTypeIcon = styled(Icon)(({ theme }) => {
  return css`
    color: ${theme.base.palette.interactive};
  `;
});

StyledResponseTypeIcon.defaultProps = defaultThemeProp;

export const StyledToggleButton = styled(Button)(({ theme }) => {
  return css`
    margin-block-start: calc(4 * ${theme.base.spacing});
    margin-inline-start: ${theme.base.spacing};
  `;
});

StyledToggleButton.defaultProps = defaultThemeProp;

const StyledResponseType = styled(MenuButton)`
  align-self: flex-start;
`;

StyledResponseType.defaultProps = defaultThemeProp;

const StyledEditor = styled(Editor)(({ theme }) => {
  return css`
    margin-block-start: calc(2 * ${theme.base.spacing});
  `;
});

StyledEditor.defaultProps = defaultThemeProp;

export const StyledModal = styled(Modal)(({ state, theme }) => {
  return css`
    ${state === 'docked'
      ? css`
          max-height: calc(100vh - ${theme.base.spacing} * 4);
          ${StyledEmailComposer} > ${StyledEditorRoot} > ${StyledRichTextEditor} > ${StyledEditorContainer} > ${StyledEditorIframe} {
            /* Equivalent to 150px minus toolbar height */
            min-height: 10rem;
            max-height: 16.5rem;
          }
        `
      : css`
          ${StyledEmailComposer} > ${StyledEditorRoot} > ${StyledRichTextEditor} > ${StyledEditorContainer} > ${StyledEditorIframe} {
            /* Equivalent to 300px minus toolbar height */
            min-height: 16.5rem;
          }
        `}

    ${StyledModalContent},
    ${StyledModalChildrenWrap},
    ${StyledEmailComposer},
    ${StyledEmailComposer} > ${StyledEditorRoot},
    ${StyledEmailComposer} > ${StyledEditorRoot} > ${StyledRichTextEditor},
    ${StyledEmailComposer} > ${StyledEditorRoot} > ${StyledRichTextEditor} > ${StyledEditorContainer} {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }

    ${StyledEmailComposer} > ${StyledEditorRoot} > ${StyledRichTextEditor} > ${StyledEditorContainer} > ${StyledEditorIframe} {
      flex-grow: 1;
    }
  `;
});

StyledModal.defaultProps = defaultThemeProp;

const responseTypeIcons = {
  reply: 'reply',
  replyAll: 'reply-all',
  forward: 'forward'
};

const editorToolbar: EditorProps['toolbar'] = [
  'inline-styling',
  'lists',
  'indentation',
  'images',
  'links'
];

const ComposerModal = ({
  modalContent,
  actions: modalActions,
  progress: modalProgress,
  subject,
  onCancel,
  compose
}: {
  modalContent: JSX.Element;
  actions: ReactNode;
  progress: ModalProps['progress'];
  subject: EmailComposerProps['data']['subject'];
  onCancel: EmailComposerProps['onCancel'];
  compose: boolean;
}) => {
  const { state } = useModalContext();
  const t = useI18n();
  return (
    <StyledModal
      heading={compose ? t('compose_label') : subject.value}
      onRequestDismiss={() => {
        onCancel();
        return false;
      }}
      actions={!modalProgress ? modalActions : undefined}
      progress={modalProgress}
      state={state}
    >
      {modalContent}
    </StyledModal>
  );
};

const createEmailTemplates = (
  selectedTemplateId: EmailComposerProps['data']['selectedTemplateId'],
  templates: EmailComposerProps['templates'] = []
): MenuProps['items'] => {
  return templates.map(template => {
    return {
      id: template.id,
      primary: template.title,
      items: template.templates
        ? createEmailTemplates(selectedTemplateId, template.templates)
        : undefined,
      selected: template.id === selectedTemplateId
    };
  });
};

const EmailComposer = forwardRef(function EmailComposer(
  {
    participants,
    senderAccounts,
    onCancel,
    onSend,
    onSave,
    onImageAdded,
    externalValidator,
    onChange,
    onFilterChange,
    onLoadMore,
    footerMoreActions,
    progress = false,
    templates,
    data: {
      to,
      cc,
      bcc,
      responseType,
      subject,
      bodyContent,
      selectedTemplateId,
      attachments,
      emailAccount,
      markAsImportant = false
    },
    handle,
    onEditorInit: onEditorInitProp,
    ...restProps
  }: PropsWithoutRef<EmailComposerProps>,
  ref: EmailComposerProps['ref']
) {
  const t = useI18n();
  const { announcePolite } = useLiveLog();
  const { create: createModal } = useModalManager();

  const prevAttachments = usePrevious(attachments);

  const [userCCToggleState, setUserCCToggleState] = useState(false);
  const [userBCCToggleState, setUserBCCToggleState] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const bodyContentToReplace = useRef<string | null>(null);
  const isProgramatic = useRef(false);
  const ccPendingFocus = useRef(false);

  const filesAnnounced = useRef(false);
  const announcedFilesSet = useRef<Set<string>>(new Set());

  const setCCHandle = useCallback((ccComboBoxHandle: ComboBoxHandleValue | null) => {
    if (ccComboBoxHandle && ccPendingFocus.current) {
      ccPendingFocus.current = false;
      ccComboBoxHandle.focusInput();
    }
  }, []);

  const filterRegex = createStringMatcher(filterValue, 'contains');

  const compose = useMemo(() => {
    return !!senderAccounts;
  }, [senderAccounts]);

  const emailTemplatesToRender = useMemo(() => {
    if (!templates) {
      return [];
    }

    const emailTemplates = createEmailTemplates(selectedTemplateId, templates);
    const newItems = filterValue
      ? menuHelpers.flatten(emailTemplates).filter(({ primary }: MenuItemProps) => {
          return filterRegex.test(primary);
        })
      : emailTemplates;

    return menuHelpers.mapTree(newItems, item => ({
      ...item,
      selected: item.items ? undefined : item.selected
    }));
  }, [filterValue, selectedTemplateId, templates]);

  const selectedEmailTemplate = useMemo(() => {
    return menuHelpers.getSelected(emailTemplatesToRender)[0];
  }, [emailTemplatesToRender]);

  const editorRef = useRef<EditorState>(null);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);
  const modalMethods = useRef<ModalMethods>();

  const responseTypeMenuData = useMemo(() => {
    const menuItems = [
      {
        id: 'reply',
        primary: t('reply'),
        visual: <StyledResponseTypeIcon name={responseTypeIcons.reply} />,
        selected: responseType === 'reply',
        onClick: () => {
          onChange('responseType', 'reply');
        }
      },
      {
        id: 'replyAll',
        primary: t('reply_all'),
        visual: <StyledResponseTypeIcon name={responseTypeIcons.replyAll} />,
        selected: responseType === 'replyAll',
        onClick: () => {
          onChange('responseType', 'replyAll');
        }
      },
      {
        id: 'forward',
        primary: t('forward'),
        visual: <StyledResponseTypeIcon name={responseTypeIcons.forward} />,
        selected: responseType === 'forward',
        onClick: () => {
          onChange('responseType', 'forward');
        }
      }
    ];
    return {
      menuItems,
      selectedResponseType: menuItems.find(item => item.selected)?.primary || menuItems[0].primary,
      selectedResponseTypeIcon:
        (responseType && responseTypeIcons[responseType]) || responseTypeIcons.reply
    };
  }, [responseType]);

  const onAttachmentDelete = (id: FileItemProps['id']) => {
    onChange('attachments', attachments?.filter(item => item.id !== id) || []);
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => ({
        File: file,
        id: createUID(),
        name: file.name
      }));
      onChange('attachments', attachments ? [...attachments, ...newFiles] : newFiles);
    }
  };

  const onEditorInit: EditorProps['onInit'] = editor => {
    if (bodyContentToReplace.current) {
      editorRef.current?.insertHtml(bodyContentToReplace.current, true);
      bodyContentToReplace.current = null;
      editorRef.current?.focus();
    }
    if (onEditorInitProp) {
      onEditorInitProp(editor);
    }
  };

  useImperativeHandle(handle, () => ({
    replaceBodyContent: (contentToReplace: string) => {
      isProgramatic.current = true;
      if (editorRef.current) {
        editorRef.current.insertHtml(contentToReplace, true);
      } else {
        modalMethods.current?.dock();
        bodyContentToReplace.current = contentToReplace;
      }
    },
    updateImage: (imageData: { src: string; alt: string }, id: string) => {
      editorRef.current?.appendImage(imageData, id);
      // Triggering onChange manually as onChange on RTE is not triggered automatically for appendImage
      onChange('bodyContent', editorRef.current?.getHtml() || '', true);
    },
    activate: () => {
      modalMethods.current?.activate();
    },
    setCursorLocationToStart: () => {
      editorRef.current?.setCursorLocationToStart?.();
    }
  }));

  const hasCC = useMemo(() => !!cc?.value?.length, [cc]);
  const hasBCC = useMemo(() => !!bcc?.value?.length, [bcc]);
  const [collapsed, setCollapsed] = useState(true);

  const content = (
    <Flex
      as={StyledEmailComposer}
      {...restProps}
      container={{
        gap: 0.5,
        direction: 'column'
      }}
      progress={!!progress}
      ref={ref}
    >
      {senderAccounts && (
        <EmailSelector
          participants={senderAccounts}
          selectedItems={emailAccount ? [emailAccount.value] : []}
          label={t('email_account')}
          mode='single-select'
          required
          onFilterChange={(filteredValue: string) => {
            onFilterChange('emailAccount', filteredValue);
          }}
          onLoadMore={(filteredValue: string) => {
            onLoadMore?.('emailAccount', filteredValue);
          }}
          onSelectedItemChange={(selectedItems: string[]) => {
            onChange('emailAccount', selectedItems[0] ?? '');
          }}
          loading={emailAccount?.loading}
          accent={emailAccount?.accent}
          status={emailAccount?.error ? 'error' : undefined}
          info={emailAccount?.error}
          compose
        />
      )}
      {responseType && (
        <StyledResponseType
          text={responseTypeMenuData.selectedResponseType}
          variant='link'
          icon={responseTypeMenuData.selectedResponseTypeIcon}
          menu={{
            mode: 'single-select',
            items: responseTypeMenuData.menuItems
          }}
        />
      )}

      <Flex
        container={{
          alignItems: 'start'
        }}
      >
        <Flex
          container={{
            direction: 'column'
          }}
          item={{
            grow: 1
          }}
        >
          <EmailSelector
            participants={participants}
            selectedItems={to?.value || []}
            label={t('to')}
            mode='multi-select'
            required
            onFilterChange={(filteredValue: string) => {
              onFilterChange('to', filteredValue);
            }}
            onLoadMore={(filteredValue: string) => {
              onLoadMore?.('to', filteredValue);
            }}
            onSelectedItemChange={(selectedItems: string[]) => {
              onChange('to', selectedItems);
            }}
            loading={to?.loading}
            accent={to?.accent}
            externalValidator={externalValidator}
            status={to?.error ? 'error' : undefined}
            info={to?.error}
          />
        </Flex>
        <StyledToggleButton
          variant='link'
          onClick={() => {
            if (collapsed) {
              ccPendingFocus.current = true;
              setUserCCToggleState(true);
              setUserBCCToggleState(true);
            }
            setCollapsed(curState => !curState);
          }}
        >
          {`${t('cc')} / ${t('bcc')}`}
        </StyledToggleButton>
      </Flex>
      <ExpandCollapse dimension='height' collapsed={collapsed} nullWhenCollapsed>
        {(userCCToggleState || hasCC) && (
          <EmailSelector
            participants={participants}
            selectedItems={cc?.value || []}
            label={t('cc')}
            mode='multi-select'
            onFilterChange={(filteredValue: string) => {
              onFilterChange('cc', filteredValue);
            }}
            onLoadMore={(filteredValue: string) => {
              onLoadMore?.('cc', filteredValue);
            }}
            loading={cc?.loading}
            accent={cc?.accent}
            onSelectedItemChange={(selectedItems: string[]) => onChange('cc', selectedItems)}
            externalValidator={externalValidator}
            status={cc?.error ? 'error' : undefined}
            info={cc?.error}
            comboBoxHandle={setCCHandle}
          />
        )}
        {(userBCCToggleState || hasBCC) && (
          <EmailSelector
            participants={participants}
            selectedItems={bcc?.value || []}
            label={t('bcc')}
            mode='multi-select'
            onFilterChange={(filteredValue: string) => {
              onFilterChange('bcc', filteredValue);
            }}
            onLoadMore={(filteredValue: string) => {
              onLoadMore?.('bcc', filteredValue);
            }}
            loading={bcc?.loading}
            accent={bcc?.accent}
            onSelectedItemChange={(selectedItems: string[]) => onChange('bcc', selectedItems)}
            externalValidator={externalValidator}
            status={bcc?.error ? 'error' : undefined}
            info={bcc?.error}
          />
        )}
      </ExpandCollapse>

      <Input
        value={subject.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('subject', e.target.value)}
        label={t('subject')}
        status={subject.error ? 'error' : undefined}
        info={subject.error}
      />

      {templates && (
        <ComboBox
          label={t('response_templates')}
          value={filterValue}
          mode='single-select'
          menu={{
            items: emailTemplatesToRender,
            onItemClick: (id: MenuItemProps['id']) => {
              setFilterValue('');
              onChange('selectedTemplateId', id);
            },
            accent: filterRegex
          }}
          selected={
            selectedEmailTemplate
              ? {
                  items: {
                    id: selectedEmailTemplate.id,
                    text: selectedEmailTemplate.primary
                  }
                }
              : undefined
          }
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (!e.target.value) onChange('selectedTemplateId', '');
            setFilterValue(e.target.value);
          }}
        />
      )}

      <StyledEditor
        autoFocus
        ref={editorRef}
        toolbar={editorToolbar}
        onImageAdded={onImageAdded}
        labelHidden
        label={t('email_message')}
        defaultValue={editorContent || bodyContent.defaultValue}
        autoResize={false}
        status={bodyContent.error ? 'error' : undefined}
        info={bodyContent.error}
        onChange={() => {
          const currentHtml = editorRef?.current?.getHtml() || '';
          setEditorContent(currentHtml);
          onChange('bodyContent', currentHtml, isProgramatic.current);
          if (isProgramatic.current) isProgramatic.current = false;
        }}
        onInit={onEditorInit}
        initOptions={{
          contentStyle: 'body { height: 100%; overflow-y: auto; }'
        }}
      />
      <Checkbox
        label={t('mark_email_as_important')}
        checked={markAsImportant}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange('markImportant', e.target.checked);
        }}
      />
      {!!attachments?.length && (
        <FileList
          type='item'
          items={attachments.map(item => {
            return {
              ...item,
              onDelete: onAttachmentDelete
            };
          })}
          ref={ref}
        />
      )}
    </Flex>
  );

  const footerContent = (
    <>
      <Button
        variant='secondary'
        onClick={() => {
          onCancel();
        }}
      >
        {t('cancel')}
      </Button>

      {footerMoreActions}

      <Flex container>
        <Button
          icon
          onClick={() => {
            if (fileUploadInputRef.current) {
              fileUploadInputRef.current.value = '';
              fileUploadInputRef.current.click();
            }
          }}
          variant='simple'
          label={t('file_upload_text_multiple')}
        >
          <Icon name='paper-clip' />
        </Button>

        {onSave && (
          <Button variant={!onSend ? 'primary' : 'secondary'} onClick={onSave}>
            {t('save_as_draft')}
          </Button>
        )}
        {onSend && (
          <Button variant='primary' onClick={onSend}>
            {t('send')}
          </Button>
        )}
      </Flex>
      <input
        type='file'
        style={{ display: 'none' }}
        ref={fileUploadInputRef}
        onChange={onFileChange}
        multiple
      />
    </>
  );

  // Create modal on mount
  useEffect(() => {
    const initiatorElement = getActiveElement();
    modalMethods.current = createModal(
      ComposerModal,
      { modalContent: content, actions: footerContent, progress, subject, onCancel, compose },
      { minimizable: true, maximizable: true, dockable: true, moveable: true }
    );

    return () => {
      // Will close the modal on unmount
      modalMethods.current?.dismiss();
      modalMethods.current?.unmount();
      if (isInstance(initiatorElement, HTMLElement) && initiatorElement.isConnected) {
        initiatorElement.focus();
      }
    };
  }, []);

  useEffect(() => {
    modalMethods.current?.update({
      modalContent: content,
      actions: footerContent,
      progress,
      subject,
      onCancel,
      compose
    });
  });

  useEffect(() => {
    if (!attachments) return;

    if (announcedFilesSet.current.size !== attachments.length) {
      filesAnnounced.current = false;
    }

    if ((prevAttachments?.length ?? 0) > attachments.length) {
      announcedFilesSet.current = new Set(
        Array.from(announcedFilesSet.current).filter(item =>
          attachments.some(file => file.id === item)
        )
      );
    }

    const errorFiles = attachments.filter(
      item => !announcedFilesSet.current.has(item.id) && item.error
    );
    const progressFiles = attachments.filter(
      item =>
        !announcedFilesSet.current.has(item.id) &&
        ('progress' in item ? typeof item.progress === 'number' && item.progress > 99 : true)
    );
    const totalAttachedFilesLength = progressFiles.length + errorFiles.length;

    if (
      totalAttachedFilesLength > 0 &&
      attachments.length - announcedFilesSet.current.size === totalAttachedFilesLength &&
      !filesAnnounced.current
    ) {
      if (progressFiles.length > 0) {
        announcePolite({
          message: t('files_uploaded', [progressFiles.length], { count: progressFiles.length })
        });

        progressFiles.forEach(file => {
          if (file.id && !announcedFilesSet.current.has(file.id)) {
            announcedFilesSet.current.add(file.id);
          }
        });
      }

      if (errorFiles.length > 0) {
        announcePolite({
          message: t('files_upload_failed', [errorFiles.length], { count: errorFiles.length })
        });

        errorFiles.forEach(file => {
          if (file.id && !announcedFilesSet.current.has(file.id)) {
            announcedFilesSet.current.add(file.id);
          }
        });
      }
      filesAnnounced.current = true;
    }
  }, [attachments]);

  return null;
});

export default EmailComposer;
